import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Stethoscope } from 'lucide-react';
import { toast } from 'sonner';

// Helper to safely extract error message
const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    return JSON.stringify(detail);
  }
  return error?.message || 'Error desconocido';
};

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    clinic_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('¡Bienvenido de vuelta!');
      navigate('/clinic/dashboard');
    } catch (error) {
      console.error('Clinic login error:', error);
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.full_name,
        'clinic_staff',
        { clinic_name: registerData.clinic_name }
      );
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/clinic/dashboard');
    } catch (error) {
      console.error('Clinic signup error:', error);
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-4">
            <Stethoscope className="h-10 w-10 text-blue-600" />
            <span className="font-bold text-2xl text-slate-900">TapPet</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Portal Clínica</h1>
          <p className="text-slate-600 mt-2">Gestiona los casos de tus pacientes</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
          </TabsList>

          {/* Login Form */}
          <TabsContent value="login">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    placeholder="tu@clinica.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contraseña
                  </label>
                  <Input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Register Form */}
          <TabsContent value="register">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre Completo
                  </label>
                  <Input
                    type="text"
                    value={registerData.full_name}
                    onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                    placeholder="Dr. Juan Pérez"
                    required
                    data-testid="register-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre de la Clínica
                  </label>
                  <Input
                    type="text"
                    value={registerData.clinic_name}
                    onChange={(e) => setRegisterData({...registerData, clinic_name: e.target.value})}
                    placeholder="Clínica Veterinaria Central"
                    required
                    data-testid="register-clinic-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    placeholder="tu@clinica.com"
                    required
                    data-testid="register-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contraseña
                  </label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>

        <p className="text-center text-sm text-slate-600 mt-6">
          <Link to="/" className="text-blue-600 hover:underline">
            Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
}
